/**
 * LCD Eligibility Alerts Panel
 * 
 * Real-time alert panel showing eligibility notifications for clinical staff.
 * Displays alerts when patient functional status drops below hospice threshold.
 * 
 * Features:
 * - Real-time WebSocket updates
 * - Severity-based prioritization
 * - One-click acknowledge/dismiss
 * - Quick navigation to patient chart
 */

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertTriangle, CheckCircle, XCircle, Bell, BellRing,
  Activity, Heart, Brain, User, Clock, ChevronRight,
  Loader2, RefreshCw, Filter, Volume2, VolumeX
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Severity config
const SEVERITY_CONFIG = {
  critical: {
    color: "text-red-400",
    bg: "bg-red-500/10",
    border: "border-red-500/30",
    icon: AlertTriangle,
    label: "Critical"
  },
  high: {
    color: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
    icon: AlertTriangle,
    label: "High"
  },
  medium: {
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-blue-500/30",
    icon: Bell,
    label: "Medium"
  },
  low: {
    color: "text-slate-400",
    bg: "bg-slate-500/10",
    border: "border-slate-500/30",
    icon: Bell,
    label: "Low"
  }
};

// Alert type config
const ALERT_TYPE_CONFIG = {
  kps_threshold: { icon: Activity, label: "KPS Threshold" },
  pps_threshold: { icon: Activity, label: "PPS Threshold" },
  kps_rapid_decline: { icon: Activity, label: "Rapid KPS Decline" },
  pps_rapid_decline: { icon: Activity, label: "Rapid PPS Decline" },
  fast_hospice_eligible: { icon: Brain, label: "FAST Hospice" },
  nyha_class_iv: { icon: Heart, label: "NYHA Class IV" }
};

export default function EligibilityAlertsPanel({ 
  isDark = true, 
  compact = false,
  onAlertClick,
  maxAlerts = 10
}) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  // States
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ critical: 0, high: 0, medium: 0, low: 0 });
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [acknowledging, setAcknowledging] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/lcd-alerts/active?limit=${maxAlerts}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
        setStats(data.by_severity || { critical: 0, high: 0, medium: 0, low: 0 });
      }
    } catch (error) {
      console.error("Failed to fetch alerts:", error);
    } finally {
      setLoading(false);
    }
  }, [token, maxAlerts]);

  // Initial fetch
  useEffect(() => {
    fetchAlerts();
    
    // Set up polling for updates (every 30 seconds)
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  // WebSocket listener for real-time alerts
  useEffect(() => {
    const handleWebSocketMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "lcd_eligibility_alert") {
          // Add new alert to the top
          setAlerts(prev => [data.data, ...prev].slice(0, maxAlerts));
          
          // Update stats
          const severity = data.data.severity || "medium";
          setStats(prev => ({
            ...prev,
            [severity]: (prev[severity] || 0) + 1
          }));
          
          // Play sound if enabled
          if (soundEnabled) {
            // Browser notification sound
            try {
              const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1sbHR0c3FtaWhra3JxbW1ub29ubG1sa2xsbG1tbW5vb3BxcnN0dXZ3eHl6e3x9fn+AgYKDhIWGh4iJiouMjY6PkJGSk5SVlpeYmZqbnJ2en6ChoqOkpaanqKmqq6ytrq+wsbKztLW2t7i5uru8vb6/wMHCw8TFxsfIycrLzM3Oz9DR0tPU1dbX2Nna29zd3t/g4eLj5OXm5+jp6uvs7e7v8PHy8/T19vf4+fr7/P3+/w==");
              audio.volume = 0.3;
              audio.play().catch(() => { /* Audio play failed - ignore */ });
            } catch (audioErr) {
              // Audio not supported - ignore
              console.debug("Audio notification not available:", audioErr);
            }
          }
          
          // Show toast
          toast.warning(data.data.title, {
            description: data.data.message?.substring(0, 100) + "...",
            duration: 5000
          });
        }
      } catch (parseErr) {
        // Message parse failed - ignore non-JSON messages
        console.debug("WebSocket message parse failed:", parseErr);
      }
    };

    // Listen to custom WebSocket events (from useRealtimeDashboard hook)
    window.addEventListener("ws:message", handleWebSocketMessage);
    return () => window.removeEventListener("ws:message", handleWebSocketMessage);
  }, [soundEnabled, maxAlerts]);

  // Acknowledge alert
  const acknowledgeAlert = async (alertId) => {
    try {
      setAcknowledging(alertId);
      const res = await fetch(`${API_URL}/api/lcd-alerts/acknowledge`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          alert_id: alertId,
          acknowledged_by: "current-user",
          action_taken: "Reviewed and acknowledged"
        })
      });

      if (res.ok) {
        setAlerts(prev => prev.filter(a => a.id !== alertId));
        toast.success("Alert acknowledged");
      }
    } catch (error) {
      toast.error("Failed to acknowledge alert");
    } finally {
      setAcknowledging(null);
    }
  };

  // Dismiss alert
  const dismissAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/lcd-alerts/dismiss/${alertId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setAlerts(prev => prev.filter(a => a.id !== alertId));
        toast.info("Alert dismissed");
      }
    } catch (error) {
      toast.error("Failed to dismiss alert");
    }
  };

  // Get total count
  const totalAlerts = stats.critical + stats.high + stats.medium + stats.low;

  // Compact header view (for dashboard widgets)
  if (compact) {
    return (
      <div className="space-y-3" data-testid="eligibility-alerts-compact">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {totalAlerts > 0 ? (
              <BellRing className="w-4 h-4 text-amber-400 animate-pulse" />
            ) : (
              <Bell className="w-4 h-4 text-slate-400" />
            )}
            <span className={`text-xs font-semibold uppercase tracking-wider ${isDark ? "text-slate-400" : "text-gray-500"}`}>
              Eligibility Alerts
            </span>
          </div>
          {totalAlerts > 0 && (
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
              {totalAlerts}
            </Badge>
          )}
        </div>

        {loading ? (
          <div className={`p-3 rounded-lg text-center ${isDark ? "bg-slate-800/50" : "bg-gray-100"}`}>
            <Loader2 className="w-4 h-4 animate-spin mx-auto text-slate-400" />
          </div>
        ) : alerts.length > 0 ? (
          <div className="space-y-2">
            {alerts.slice(0, 3).map((alert) => {
              const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.medium;
              const SeverityIcon = config.icon;
              
              return (
                <div
                  key={alert.id}
                  className={`p-2 rounded-lg border cursor-pointer transition-all hover:scale-[1.02] ${config.bg} ${config.border}`}
                  onClick={() => {
                    setSelectedAlert(alert);
                    onAlertClick?.(alert);
                  }}
                >
                  <div className="flex items-start gap-2">
                    <SeverityIcon className={`w-4 h-4 mt-0.5 ${config.color}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-medium truncate ${config.color}`}>
                        {alert.title}
                      </p>
                      <p className={`text-xs truncate ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        {alert.patient_name}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {alerts.length > 3 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full text-xs"
                onClick={() => setShowAllAlerts(true)}
              >
                View all {totalAlerts} alerts
                <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        ) : (
          <div className={`p-3 rounded-lg text-center ${isDark ? "bg-slate-800/50" : "bg-gray-100"}`}>
            <CheckCircle className="w-5 h-5 mx-auto mb-1 text-emerald-400" />
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
              No active alerts
            </p>
          </div>
        )}

        {/* All Alerts Sheet */}
        <Sheet open={showAllAlerts} onOpenChange={setShowAllAlerts}>
          <SheetContent side="right" className={`w-[500px] ${isDark ? "bg-slate-900 border-slate-800" : "bg-white"}`}>
            <SheetHeader>
              <SheetTitle className={`flex items-center gap-2 ${isDark ? "text-white" : ""}`}>
                <BellRing className="w-5 h-5 text-amber-400" />
                Eligibility Alerts
                <Badge className="ml-2 bg-amber-500/20 text-amber-400">{totalAlerts}</Badge>
              </SheetTitle>
              <SheetDescription>
                Patients with functional status below hospice eligibility threshold
              </SheetDescription>
            </SheetHeader>
            
            {/* Stats Summary */}
            <div className="grid grid-cols-4 gap-2 mt-4 mb-4">
              {Object.entries(stats).map(([severity, count]) => {
                const config = SEVERITY_CONFIG[severity];
                return (
                  <div key={severity} className={`p-2 rounded-lg text-center ${config.bg} ${config.border} border`}>
                    <p className={`text-lg font-bold ${config.color}`}>{count}</p>
                    <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>{config.label}</p>
                  </div>
                );
              })}
            </div>

            <ScrollArea className="h-[calc(100vh-250px)]">
              <div className="space-y-3 pr-4">
                {alerts.map((alert) => {
                  const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.medium;
                  const SeverityIcon = config.icon;
                  const typeConfig = ALERT_TYPE_CONFIG[alert.alert_type] || { icon: Bell, label: alert.alert_type };
                  const TypeIcon = typeConfig.icon;
                  
                  return (
                    <Card key={alert.id} className={`${config.bg} ${config.border} border`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800" : "bg-white"}`}>
                            <SeverityIcon className={`w-5 h-5 ${config.color}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className={`font-medium ${config.color}`}>{alert.title}</p>
                              <Badge variant="outline" className="text-xs">
                                <TypeIcon className="w-3 h-3 mr-1" />
                                {typeConfig.label}
                              </Badge>
                            </div>
                            <p className={`text-sm mt-1 ${isDark ? "text-slate-300" : "text-gray-700"}`}>
                              {alert.message}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {alert.patient_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(alert.created_at).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 mt-2">
                              <Badge className={`${config.bg} ${config.color} border ${config.border}`}>
                                Current: {alert.current_value}
                              </Badge>
                              <Badge variant="outline">
                                Threshold: {alert.threshold_value}
                              </Badge>
                            </div>
                            <div className="flex gap-2 mt-3">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => dismissAlert(alert.id)}
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                Dismiss
                              </Button>
                              <Button 
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => acknowledgeAlert(alert.id)}
                                disabled={acknowledging === alert.id}
                              >
                                {acknowledging === alert.id ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                )}
                                Acknowledge
                              </Button>
                              <Button 
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/lcd-eligibility?patient=${alert.patient_id}`)}
                              >
                                Evaluate
                                <ChevronRight className="w-3 h-3 ml-1" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </ScrollArea>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // Full panel view
  return (
    <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"} data-testid="eligibility-alerts-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            {totalAlerts > 0 ? (
              <BellRing className="w-5 h-5 text-amber-400 animate-pulse" />
            ) : (
              <Bell className="w-5 h-5 text-slate-400" />
            )}
            Eligibility Alerts
            {totalAlerts > 0 && (
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                {totalAlerts}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setSoundEnabled(!soundEnabled)}
            >
              {soundEnabled ? (
                <Volume2 className="w-4 h-4 text-emerald-400" />
              ) : (
                <VolumeX className="w-4 h-4 text-slate-400" />
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={fetchAlerts}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {Object.entries(stats).map(([severity, count]) => {
            const config = SEVERITY_CONFIG[severity];
            return (
              <div key={severity} className={`p-2 rounded-lg text-center ${config.bg}`}>
                <p className={`text-xl font-bold ${config.color}`}>{count}</p>
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>{config.label}</p>
              </div>
            );
          })}
        </div>

        {/* Alerts List */}
        <ScrollArea className="h-64">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : alerts.length > 0 ? (
            <div className="space-y-3">
              {alerts.map((alert) => {
                const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.medium;
                const SeverityIcon = config.icon;
                
                return (
                  <div
                    key={alert.id}
                    className={`p-3 rounded-lg border ${config.bg} ${config.border}`}
                  >
                    <div className="flex items-start gap-3">
                      <SeverityIcon className={`w-5 h-5 mt-0.5 ${config.color}`} />
                      <div className="flex-1">
                        <p className={`font-medium ${config.color}`}>{alert.title}</p>
                        <p className={`text-sm ${isDark ? "text-slate-300" : "text-gray-600"}`}>
                          {alert.patient_name}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {alert.current_value}
                          </Badge>
                          <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                            {new Date(alert.created_at).toLocaleTimeString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => acknowledgeAlert(alert.id)}
                          disabled={acknowledging === alert.id}
                        >
                          {acknowledging === alert.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <CheckCircle className="w-12 h-12 text-emerald-400/50 mb-2" />
              <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                No active eligibility alerts
              </p>
              <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                All patients are within normal parameters
              </p>
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
